import React, { Component } from 'react';
import { EyeIcon, PencilIcon, ArrowPathIcon, UserIcon, EnvelopeIcon, ArchiveBoxIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import CreateMode from './CreateMode';
import ModuleList from './ModuleList';
import outlookService from '../../services/outlookService';
import apiService from '../../services/apiService';
import storageService from '../../services/storageService';
import authService from '../../services/authService';
import ShowHeadFields from './ShowHeadFields';
import ConfirmationNotification from '../common/ConfirmationNotification';

class SearchResults extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showCreateMode: false,
      archivingToRecord: null,
      showArchiveDialog: false,
      recordToArchive: null,
      showArchiveNotification: false,
      notificationMessage: '',
    };
  }

  componentDidMount() {
  }

  handleArchiveEmailClick = (record) => {
    this.setState({ 
      showArchiveDialog: true,
      recordToArchive: record
    });
  };

  handleArchiveEmailConfirm = async () => {
    const record = this.state.recordToArchive;
    if (!record) return;

    try {
      this.setState({ 
        showArchiveDialog: false,
        archivingToRecord: record.crmid,
        recordToArchive: null
      });

      const user = await storageService.getUser();

      // Validate Office.js context before proceeding
      if (!Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
        throw new Error('Office.js context not available. Please refresh the add-in.');
      }
      
      // Get complete email data including attachments, CC, BCC, etc.
      const completeEmailData = await outlookService.createCompleteEmailArchiveData(
        record.crmid,
        user.userid
      );

      const result = await apiService.archiveEmail(completeEmailData);

      // Check if recordid exists (either as string or object with recordid property)
      const recordId = result.data?.recordid;
      const hasValidRecordId = recordId && (
        (typeof recordId === 'string' && recordId.length > 0) ||
        (typeof recordId === 'object' && recordId.recordid)
      );

      if (result.success && hasValidRecordId) {
        const attachmentText = completeEmailData.hasAttachments 
          ? ` with ${completeEmailData.attachments.length} attachment(s)` 
          : '';
        
        this.setState({
          showArchiveNotification: true,
          notificationMessage: `Email archived successfully to ${record.label}${attachmentText}`,
          archivingToRecord: null
        });
      } else if (result.needsLogout && this.props.onLogout) {
        this.props.onLogout();
      } else {
        this.setState({
          showArchiveNotification: true,
          notificationMessage: 'Failed to archive email. Please try again.',
          archivingToRecord: null
        });
      }
    } catch (error) {
      this.setState({
        showArchiveNotification: true,
        notificationMessage: 'Failed to archive email. Please try again.',
        archivingToRecord: null
      });
    }
  };

  handleArchiveEmailCancel = () => {
    this.setState({ 
      showArchiveDialog: false,
      recordToArchive: null
    });
  };

  handleRecordClick = (record) => {
    // Don't automatically open detail view - let users choose via action buttons
  };

  handleArchiveNotificationComplete = () => {
    this.setState({ showArchiveNotification: false });
  };

  handleCreateRecord = (moduleKey) => {
    this.setState({ 
      showCreateMode: true,
      preSelectedModule: moduleKey
    });
  };

  render() {
    const { results } = this.props;
    const { 
      showCreateMode, 
      archivingToRecord, 
      showArchiveDialog,
      recordToArchive,
      showArchiveNotification,
      notificationMessage,
    } = this.state;
    
    // Handle different result formats - could be array or single object
    let processedResults = [];
    if (Array.isArray(results)) {
      if (results.length === 1 && Array.isArray(results[0]) && results[0].length === 0) {
        // Handle case where results is [Array(0)] - means no results
        processedResults = [];
      } else {
        processedResults = results;
      }
    } else if (results && typeof results === 'object' && results.label) {
      // Single result object
      processedResults = [results];
    }
    
    const totalCount = processedResults.length;
    
    // Get email info for create mode
    const { firstname, lastname } = outlookService.parseDisplayName();
    const fromEmail = outlookService.getFromEmail();

    if (showCreateMode) {
      return (
        <CreateMode
          preSelectedModule={this.state.preSelectedModule}
          firstname={firstname}
          lastname={lastname}
          fromEmail={fromEmail}
          onCancel={() => this.setState({ showCreateMode: false, preSelectedModule: null })}
          onCheckingMail={() => {
            this.setState({ showCreateMode: false, preSelectedModule: null });
            this.props.onCheckingMail();
          }}
          onLogout={this.props.onLogout}
          {...this.props}
        />
      );
    }

    // If no results, show module list directly for creation
    if (totalCount === 0) {
      const enabledModules = authService.getEnabledModules();

      return (
        <div className="space-y-4">
          {/* Email Info Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-primary-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {`${firstname} ${lastname}`}
                </p>
                <div className="flex items-center text-xs text-gray-500">
                  <EnvelopeIcon className="w-3 h-3 mr-1" />
                  <span className="truncate">{fromEmail}</span>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-red-600">
                    No Records Found
                  </p>
                </div>
              </div>
            </div>

            {/* Add Contact/Lead Buttons - Only show when no results */}
            <div className="mt-4 space-y-3">
              {/* Add Contact Button */}
              {enabledModules.Contacts && enabledModules.Contacts.is_create_view_permitted && (enabledModules.Contacts.cancreate === 1 || enabledModules.Contacts.cancreate === '1') && (
                <button
                  onClick={() => this.handleCreateRecord('Contacts')}
                  className="w-full flex items-center justify-start px-3 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm text-left"
                  style={{ backgroundColor: '#339fcf' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0075a8'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#339fcf'}
                >
                  <UserIcon className="w-4 h-4 mr-2" />
                  Add Contact
                </button>
              )}

              {/* Add Leads Button */}
              {enabledModules.Leads && enabledModules.Leads.is_create_view_permitted && (enabledModules.Leads.cancreate === 1 || enabledModules.Leads.cancreate === '1') && (
                <button
                  onClick={() => this.handleCreateRecord('Leads')}
                  className="w-full flex items-center justify-start px-3 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm text-left"
                  style={{ backgroundColor: '#66b7db' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#339fcf'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#66b7db'}
                >
                  <UserIcon className="w-4 h-4 mr-2" />
                  Add Lead
                </button>
              )}
            </div>
          </div>

          {/* Refresh Button */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <button
              className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              onClick={this.props.onCheckingMail}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Refresh Search
            </button>
          </div>
        </div>
      );
    }

    // Multiple results - show list
    return (
      <>
        {/* Success Notifications */}
        <ConfirmationNotification
          show={showArchiveNotification}
          message={notificationMessage}
          type="archive"
          onComplete={this.handleArchiveNotificationComplete}
          duration={2500}
        />
        
        <div className="space-y-4">
        {/* Archive Email Confirmation Dialog */}
        <Transition appear show={showArchiveDialog} as={React.Fragment}>
          <Dialog as="div" className="relative z-10" onClose={this.handleArchiveEmailCancel}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="ml-3">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Archive Email
                        </Dialog.Title>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to archive this email to{' '}
                        <span className="font-medium text-gray-900">
                          {recordToArchive?.label || 'this record'}
                        </span>
                        ? This action will save the email and associate it with the selected record.
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
                        onClick={this.handleArchiveEmailCancel}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm"
                        style={{ backgroundColor: '#0075a8' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#005e86'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#0075a8'}
                        onClick={this.handleArchiveEmailConfirm}
                      >
                        Archive Email
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Search Results Status Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            {totalCount > 0 ? (
              totalCount === 1 
                ? `1 record found for ${fromEmail}`
                : `${totalCount} records found for ${fromEmail}`
            ) : (
              `No record found for ${fromEmail}`
            )}
          </p>
        </div>

        {/* Search Results - Show Records with Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="divide-y divide-gray-200">
            {processedResults.map((record, index) => (
              <div key={index} className="px-4 py-4">
                {/* Email Information */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <UserIcon className="w-5 h-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-gray-900 truncate">
                      {`${firstname} ${lastname}`}
                    </p>
                    <div className="flex items-center text-sm text-gray-500">
                      <EnvelopeIcon className="w-4 h-4 mr-1" />
                      <span className="truncate">{fromEmail}</span>
                    </div>
                    {record.module && (
                      <p className="text-xs text-gray-500 mt-1">
                        Record found in {record.module}
                      </p>
                    )}
                  </div>
                  
                  {/* Action Icons */}
                  <div className="flex items-center space-x-1">
                    {/* View Details Icon */}
                    {record.viewable && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Pass record with view mode flag
                          const viewRecord = {
                            ...record,
                            viewMode: true,
                            crmid: record.crmid || record.id,
                            module: record.module || this.getModuleFromRecord(record)
                          };
                          this.props.onRecordSelect(viewRecord);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    )}
                    {/* Edit Icon */}
                    {record.editable && (
                      <button
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Pass minimal edit info - let EntityForm handle the API call
                          const editRecord = {
                            editMode: true,
                            crmid: record.crmid || record.id,
                            module: record.module || this.getModuleFromRecord(record),
                            label: record.label || record.name
                          };
                          this.props.onRecordSelect(editRecord);
                        }}
                        title="Edit Record"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Log Email Button - Only show if results found */}
                {totalCount > 0 && processedResults.length > 0 && record.label && (
                  <div className="mt-4">
                    <button
                      className="w-full flex items-center justify-left px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-left"
                      style={{ backgroundColor: '#339fcf' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#0075a8'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#339fcf'}
                      onClick={() => this.handleArchiveEmailClick(record)}
                      disabled={archivingToRecord === record.crmid}
                    >
                      {archivingToRecord === record.crmid ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Logging...
                        </>
                      ) : (
                        <>
                          <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                          Log Email for {record.label}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Refresh Button */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <button
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
            onClick={this.props.onCheckingMail}
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Refresh Search
          </button>
        </div>
      </div>
      </>
    );
  }

  getModuleFromRecord = (record) => {
    // Try to determine module from record structure
    if (record.module) return record.module;
    if (record.record_module) return record.record_module;
    // Fallback - could be determined from context or API response
    return 'Contacts'; // Default fallback
  };
}

export default SearchResults;